// Read-only floor product card (Module 01 S9, §4). Shows SKU, name, status,
// attributes, and the active routing steps for the worker's facility with their
// instructions. It renders NO cost and NO BOM quantities (§4 S9 / §7): the floor
// sees per-WO material lists from module 04, not catalogue cost — so this
// component never reads a `*_minor` field even if one were present on the object.

import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { StatusBadge } from "../../../components/status-badge";

type Product = components["schemas"]["Product"];
type Routing = components["schemas"]["Routing"];
type RoutingStep = components["schemas"]["RoutingStep"];

function humanizeKey(key: string): string {
  return key.replace(/_/g, " ");
}

function formatAttrValue(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
}

/** Best-effort text extraction from a step's loose `instructions` jsonb. Images
 * (S3 keys) are not shown on the card — they are unavailable offline. */
function instructionLines(instructions: Record<string, unknown>): string[] {
  const lines: string[] = [];
  const text = instructions["text"];
  if (typeof text === "string" && text.trim().length > 0) lines.push(text);
  const blocks = instructions["blocks"];
  if (Array.isArray(blocks)) {
    for (const block of blocks) {
      if (typeof block === "string") {
        if (block.trim().length > 0) lines.push(block);
      } else if (typeof block === "object" && block !== null) {
        const blockText = (block as Record<string, unknown>)["text"];
        if (typeof blockText === "string" && blockText.trim().length > 0) lines.push(blockText);
      }
    }
  }
  return lines;
}

function StepFlags({ step }: { step: RoutingStep }) {
  const { t } = useTranslation();
  const flags: string[] = [];
  if (step.is_outsourced) flags.push(t("catalogue.floorLookup.card.outsourced"));
  if (step.requires_measured_capture) flags.push(t("catalogue.floorLookup.card.measured"));
  if (step.requires_serial_capture) flags.push(t("catalogue.floorLookup.card.serial"));
  if (step.qc_control_point_id !== null) flags.push(t("catalogue.floorLookup.card.qcGate"));
  if (flags.length === 0) return null;
  return (
    <div className="floor-step-flags">
      {flags.map((flag) => (
        <span className="floor-flag" key={flag}>
          {flag}
        </span>
      ))}
    </div>
  );
}

function RoutingSteps({ routing }: { routing: Routing | undefined }) {
  const { t } = useTranslation();
  const steps =
    routing === undefined ? [] : [...routing.steps].sort((a, b) => a.step_no - b.step_no);

  if (steps.length === 0) {
    return <p className="floor-empty-line">{t("catalogue.floorLookup.card.noRouting")}</p>;
  }

  return (
    <ol className="floor-steps">
      {steps.map((step) => {
        const lines = instructionLines(step.instructions);
        return (
          <li className="floor-step" key={step.id} data-testid="routing-step">
            <div className="floor-step-head">
              <span className="floor-step-no">
                {t("catalogue.floorLookup.card.step", { no: step.step_no })}
              </span>
              <span className="floor-step-name">{step.name}</span>
            </div>
            <StepFlags step={step} />
            {lines.length > 0 ? (
              <div className="floor-step-instructions">
                {lines.map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            ) : (
              <p className="floor-step-instructions floor-empty-line">
                {t("catalogue.floorLookup.card.noInstructions")}
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export function ProductCard({
  product,
  routing,
}: {
  product: Product;
  routing: Routing | undefined;
}) {
  const { t } = useTranslation();

  const attributes = Object.entries(product.attributes)
    .map(([key, value]) => ({ key, display: formatAttrValue(value) }))
    .filter((entry): entry is { key: string; display: string } => entry.display !== undefined);

  return (
    <article className="floor-card" data-testid="floor-product-card">
      <header className="floor-card-head">
        <div className="floor-card-ident">
          <span className="floor-card-sku" data-testid="card-sku">
            {product.sku}
          </span>
          <StatusBadge status={product.status} />
        </div>
        <h2 className="floor-card-name">{product.name}</h2>
      </header>

      <section className="floor-card-section">
        <h3 className="floor-card-heading">{t("catalogue.floorLookup.card.attributes")}</h3>
        {attributes.length > 0 ? (
          <dl className="floor-attrs">
            {attributes.map((entry) => (
              <div className="floor-attr" key={entry.key}>
                <dt className="floor-attr-key">{humanizeKey(entry.key)}</dt>
                <dd className="floor-attr-value">{entry.display}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="floor-empty-line">{t("catalogue.floorLookup.card.noAttributes")}</p>
        )}
      </section>

      <section className="floor-card-section">
        <h3 className="floor-card-heading">{t("catalogue.floorLookup.card.routing")}</h3>
        <RoutingSteps routing={routing} />
      </section>
    </article>
  );
}
